import React, { useEffect, useRef } from "react";
import { Row, Col, Container } from "react-bootstrap";
import ContainerWide from "../components/ContainerWide";
import WpImageFluid from "../components/WpImageFluid";
import { VectorBackground } from "../components/VectorBackground";
import { Autoplay } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";

import "swiper/css";
import "swiper/css/autoplay";

const filterTrustedByLogos = (trustedByLogos) =>
    trustedByLogos?.filter((logo) => logo?.image) || [];

const filterTestimonials = (testimonials) =>
    testimonials?.filter(
        (testimonial) => testimonial?.quote || testimonial?.companyLogo
    ) || [];

const filterStats = (stats) =>
    stats?.filter((stat) => stat?.number || stat?.label) || [];

export const hasSocialProofContent = ({
    trustedByLogos,
    testimonials,
    stats,
}) =>
    filterTrustedByLogos(trustedByLogos).length > 0 ||
    filterTestimonials(testimonials).length > 0 ||
    filterStats(stats).length > 0;

const SocialProof = ({
    category,
    trustedByHeader,
    trustedByLogos,
    testimonials,
    stats,
    statsHeader,
    className,
    id,
    autoplayDelay = 4000,
}) => {
    const swiperRef = useRef(null);
    const sliderRef = useRef(null);
    const validTrustedByLogos = filterTrustedByLogos(trustedByLogos);
    const validTestimonials = filterTestimonials(testimonials);
    const validStats = filterStats(stats);

    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    swiperRef.current?.autoplay.resume();
                } else {
                    swiperRef.current?.autoplay.pause();
                }
            },
            { threshold: 0.3 }
        );

        if (sliderRef.current) {
            observer.observe(sliderRef.current);
        }

        return () => {
            if (sliderRef.current) {
                observer.unobserve(sliderRef.current);
            }
        };
    }, []);

    if (
        !hasSocialProofContent({
            trustedByLogos,
            testimonials,
            stats,
        })
    ) {
        return null;
    }

    return (
        <div
            className={`social-proof ${className || ""}`}
            id={id || "social-proof"}
        >
            <VectorBackground />
            <Container>
                {(category || trustedByHeader) && (
                    <Row className="social-proof__header-row">
                        <Col>
                            {category && (
                                <p
                                    className="social-proof__label"
                                    dangerouslySetInnerHTML={{
                                        __html: category,
                                    }}
                                />
                            )}
                            {trustedByHeader && (
                                <h2
                                    className="social-proof__header"
                                    dangerouslySetInnerHTML={{
                                        __html: trustedByHeader,
                                    }}
                                />
                            )}
                        </Col>
                    </Row>
                )}
                {validTrustedByLogos.length > 0 && (
                    <Row className="social-proof__logos-row">
                        <Col>
                            <div className="social-proof__logos">
                                {validTrustedByLogos.map((logo, index) => (
                                    <WpImageFluid
                                        key={index}
                                        image={logo.image}
                                        className="social-proof__logo"
                                        alt={logo.image?.altText || ""}
                                    />
                                ))}
                            </div>
                        </Col>
                    </Row>
                )}
                {statsHeader && validStats.length > 0 && (
                    <Row className="social-proof__stats-header-row">
                        <Col>
                            <h3
                                className="social-proof__stats-header"
                                dangerouslySetInnerHTML={{
                                    __html: statsHeader,
                                }}
                            />
                        </Col>
                    </Row>
                )}
                {validStats.length > 0 && (
                    <Row className="social-proof__stats-row">
                        {validStats.map((stat, index) => (
                            <Col
                                key={index}
                                xs={24}
                                lg={8}
                                className="social-proof__stat-col"
                            >
                                <div className="social-proof__stat">
                                    <span className="social-proof__stat-number">
                                        {stat.number}
                                    </span>
                                    <span className="social-proof__stat-label">
                                        {stat.label}
                                    </span>
                                </div>
                            </Col>
                        ))}
                    </Row>
                )}
            </Container>
            {validTestimonials.length > 0 && (
                <Container>
                    <div
                        className="social-proof__testimonials-row"
                        ref={sliderRef}
                    >
                        <Swiper
                            onBeforeInit={(swiper) => {
                                swiperRef.current = swiper;
                            }}
                            modules={[Autoplay]}
                            spaceBetween={30}
                            slidesPerView={1}
                            loop={true}
                            autoplay={{
                                delay: autoplayDelay,
                                disableOnInteraction: false,
                                pauseOnMouseEnter: true,
                            }}
                            breakpoints={{
                                768: {
                                    slidesPerView: 2,
                                    spaceBetween: 40,
                                },
                            }}
                            className="social-proof__testimonials-slider"
                        >
                            {validTestimonials.map((testimonial, index) => (
                                <SwiperSlide key={index}>
                                    <div className="social-proof__testimonial-card">
                                        <blockquote
                                            className="social-proof__testimonial-quote"
                                            dangerouslySetInnerHTML={{
                                                __html: testimonial.quote,
                                            }}
                                        />
                                        {testimonial.companyLogo && (
                                            <div className="social-proof__testimonial-logo">
                                                <WpImageFluid
                                                    image={
                                                        testimonial.companyLogo
                                                    }
                                                    alt={
                                                        testimonial.companyLogo
                                                            ?.altText || ""
                                                    }
                                                />
                                            </div>
                                        )}
                                    </div>
                                </SwiperSlide>
                            ))}
                        </Swiper>
                    </div>
                </Container>
            )}
        </div>
    );
};

export default SocialProof;
